import {
  atom,
  useRecoilState,
  useRecoilValue,
  selector,
  useResetRecoilState
} from 'recoil'
import { endOfDay, startOfDay } from 'date-fns'
import { sitesState } from 'store'

export interface Coupon {
  id: string
  correlation_id?: string
  phone?: string
  expired: boolean
  disabled: boolean
  available_soon: number
  valid: boolean
  available: boolean
  used: boolean
  starts_at?: string
  ends_at?: string
  validity_period: number
  code: string
  site: { name: string }
  offer?: { name: string }
  number_of_devices: number
  description: string
  created_at: string
  creator_firstname?: string
  creator_lastname?: string
  created_by: string
}

const filtersState = atom({
  key: 'CouponsFilters',
  default: {
    createdAt: {
      start: null,
      end: null,
    },
    usageDate: {
      start: null,
      end: null,
    },
    verbatim: '',
    status: [],
  },
})

const sortState = atom({
  key: 'CouponsSort',
  default: {
    by: 'created_at',
    direction: 'desc',
  },
})

const couponsSelectedState = atom<Coupon[]>({
  key: 'CouponsSelected',
  default: [],
})

const lastCouponCheckedState = atom<Coupon | null>({
  key: 'CouponsLastCouponChecked',
  default: null
})

const queryVariablesState = selector({
  key: 'CouponsQueryVariables',
  get: ({ get }) => {
    const filters = get(filtersState)
    const sort = get(sortState)
    const { selected } = get(sitesState)

    return {
      verbatim: filters.verbatim.length > 2 ? filters.verbatim : '',
      created_at_start: filters.createdAt.start ? startOfDay(filters.createdAt.start).toISOString() : null,
      created_at_end: filters.createdAt.end ? endOfDay(filters.createdAt.end).toISOString() : null,
      usage_date_start: filters.usageDate.start ? startOfDay(filters.usageDate.start).toISOString() : null,
      usage_date_end: filters.usageDate.end ? endOfDay(filters.usageDate.end).toISOString() : null,
      availability: filters.status.join(','),
      sort_by: sort.by,
      sort_direction: sort.direction,
      sf_ids: selected.map(({ sf_id }) => sf_id)
    }
  },
})

const useFilters = () => {
  return useRecoilState(filtersState)
}

export const useResetFilters = () => {
  return useResetRecoilState(filtersState)
}

export const useQueryVariables = () => useRecoilValue(queryVariablesState)

export const useSort = () => {
  const [{ direction, by }, setSort] = useRecoilState(sortState)
  return [
    { direction, by },
    (sort) => {
      if (by === sort) {
        const newDirection = direction === 'asc' ? 'desc' : 'asc'
        setSort({ by: sort, direction: newDirection })
      } else {
        setSort({ by: sort, direction: 'asc' })
      }
    },
  ]
}

export const useCouponsSelected = () => {
  return useRecoilState(couponsSelectedState);
}

export const useLastCouponChecked = () => {
  return useRecoilState(lastCouponCheckedState);
}

export default useFilters
